import React, { useEffect, useRef, useState } from 'react'
import styled from '@emotion/styled'
import { InputGroup } from '../Controls'
import { Controller, UseFormReturn } from 'react-hook-form'
import { CheckoutForm, FormFields, FORM_DEFAULTS } from './types'
import { HalfWidthFlexContainer } from './commonStyles'
import {
  RECIPIENT_INPUTS,
  handlePhoneNumberOnBlur,
  handlePhoneNumberOnChange,
  isInternationalNumber
} from './utils'
import FormInput from '../Inputs/FormInput'
import { IntlPhoneNumber } from '../Inputs'
import { useConsumerCart, useMediaQueries } from 'shop/hooks'
import { scrollToElement } from '../Controls/utils'
import CollapsedPersonDetails from './CollapsedPersonDetails'
import { strippedContactNumPrefix } from '../Inputs/utils'
import { GoPeople } from 'react-icons/go'
import ExpandableDiv from '../ExpandableContainer/ExpandableDiv'
import ToggleSwitchLabel from '../Forms/ToggleSwitchLabel'
import GenericBreakdownItem from '../GenericBreakdownItem/GenericBreakdownItem'
import { VisibileToggleDiv } from '../common'
import { LineBreak } from '../Shop/commonStyles'
import {
  amendRecipientDetails,
  handleFormFieldCartUpdate
} from './formCartUpdates'
import { cloneDeep } from 'lodash'
import { useFocusOutListener } from 'shop/hooks/useFocusOutListener'
import AlertMessage from '../shared/AlertMessage/AlertMessage'

type Props = {
  formHandle: UseFormReturn<FormFields>
  animationsReady: boolean
  prevRecipientDetailsRef: React.MutableRefObject<
    CheckoutForm['recipientDetails'] | undefined
  >
}

const RecipientDetails = ({
  formHandle,
  animationsReady,
  prevRecipientDetailsRef
}: Props) => {
  const {
    register,
    formState: { errors },
    clearErrors,
    watch,
    trigger,
    setValue,
    control
  } = formHandle
  const forSomeoneElse = watch('forSomeoneElse')
  const recipientDetails = watch('recipientDetails')
  const formInitialised = watch('__formInitialised')
  const { isMobile } = useMediaQueries()
  const { cart, updateConsumerCart } = useConsumerCart()

  const detailsErrors = errors['recipientDetails']
  const isMounted = useRef(false) // stops scroll trigger on page load
  const [isRecipientDetailsExpanded, setIsRecipientDetailsExpanded] =
    useState<boolean>(false)
  const sectionRef = useRef<HTMLDivElement>(null)

  const { firstNameInputId, lastNameInputId, phonePrefixId, phoneInputId } =
    RECIPIENT_INPUTS

  const { firstName, lastName, contactNumber } = recipientDetails
  const hasAllDetails = !!firstName && !!lastName && !!contactNumber

  const handlePhoneInputsBlur = (
    e: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    handlePhoneNumberOnBlur(
      e,
      setValue,
      trigger,
      'recipientDetails.contactNumber'
    )
  }

  // Expand the form on load if incomplete or with errors
  useEffect(() => {
    if (
      (detailsErrors || !hasAllDetails) &&
      formInitialised &&
      forSomeoneElse
    ) {
      setIsRecipientDetailsExpanded(true)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [detailsErrors, hasAllDetails, formInitialised])

  useEffect(() => {
    if (hasAllDetails) return
    setIsRecipientDetailsExpanded(forSomeoneElse)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [forSomeoneElse])

  // Set listener for when the form section loses focus to trigger update cart and collapse.
  const handleFocusOut = async (e: FocusEvent) => {
    handleFormFieldCartUpdate({
      formRef: sectionRef,
      checkoutForm: formHandle,
      event: e,
      prevFieldValue: prevRecipientDetailsRef,
      formFieldName: 'recipientDetails',
      updateConsumerCart,
      handleCloseSection: () => setIsRecipientDetailsExpanded(false)
    })
  }
  useFocusOutListener({ sectionRef, onFocusOut: handleFocusOut })

  useEffect(() => {
    if (forSomeoneElse && isMobile && !!isMounted.current) {
      // scroll to section when toggling it on for Mobile
      scrollToElement('recipient-details-section', 80)
    }
    isMounted.current = true // stops scroll trigger on page load
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [forSomeoneElse])

  // When toggling off "ordering for someone else", clear the recipient input errors and update cart values.
  useEffect(() => {
    if (!formInitialised) return
    if (!forSomeoneElse) {
      // update cart if not already null
      if (cart?.recipientDetails !== null) {
        updateConsumerCart({
          recipientDetails: null
        })
      }
      clearErrors('recipientDetails')
      prevRecipientDetailsRef.current = FORM_DEFAULTS['recipientDetails']
    } else {
      if (!cart?.recipientDetails?.firstName && hasAllDetails) {
        const amendedRecipientDetails = amendRecipientDetails(recipientDetails)
        if (amendedRecipientDetails) {
          updateConsumerCart({ recipientDetails: amendedRecipientDetails })
          prevRecipientDetailsRef.current = cloneDeep(recipientDetails)
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [forSomeoneElse])

  if (!formInitialised) return <></>

  return (
    <>
      <VisibileToggleDiv isVisible={true}>
        <LineBreak />
      </VisibileToggleDiv>
      <Fields>
        <Controller
          name='forSomeoneElse'
          control={control}
          render={({ field }) => (
            <ToggleSwitchLabel
              switchName={field.name}
              label={'Order for someone else'}
              onToggle={field.onChange}
              defaultChecked={field.value}
            />
          )}
        />
        <ExpandableDiv
          isExpanded={
            !!forSomeoneElse && !isRecipientDetailsExpanded && hasAllDetails
          }
          collapsedHeight={0}
          transition={animationsReady ? undefined : 'none'}
        >
          <CollapsedDetailsContainer>
            <GenericBreakdownItem
              label={
                <CollapsedPersonDetails
                  firstName={recipientDetails.firstName}
                  lastName={recipientDetails.lastName}
                  contactNumber={`${strippedContactNumPrefix(recipientDetails.contactNumPrefix)}${recipientDetails.contactNumber}`}
                />
              }
              maxWidth={'100%'}
              withEllipsis={false}
              withLineBreak={false}
              LeftIcon={GoPeople}
              padding={'0'}
              onChangeButtonClick={() => setIsRecipientDetailsExpanded(true)}
            />
          </CollapsedDetailsContainer>
        </ExpandableDiv>
        <ExpandableDiv
          isExpanded={forSomeoneElse && isRecipientDetailsExpanded}
          collapsedHeight={0}
          transition={animationsReady ? undefined : 'none'}
          id={'recipient-details-expandable-div'}
        >
          <InputsContainer ref={sectionRef}>
            <InputGroup templateColumns='1fr' gridGap='18px'>
              <HalfWidthFlexContainer>
                <FormInput
                  id={firstNameInputId}
                  labelText='Recipient First Name'
                  name={firstNameInputId}
                  value={recipientDetails?.firstName || ''}
                  formRef={register('recipientDetails.firstName', {
                    required: forSomeoneElse
                      ? "Please enter the recipient's first name"
                      : false,
                    validate: (value: string) =>
                      value.trim().length === 0 && forSomeoneElse
                        ? "Please enter the recipient's first name"
                        : true
                  })}
                  showError={!!errors?.recipientDetails?.firstName}
                  scrollSettings={{
                    scrollOnFocus: true,
                    scrollToElementId: isMobile
                      ? 'recipient-details-section'
                      : 'details-section'
                  }}
                  tabIndex={!isRecipientDetailsExpanded ? -1 : 0}
                />
                <FormInput
                  id={lastNameInputId}
                  labelText='Recipient Last Name'
                  name={lastNameInputId}
                  value={recipientDetails?.lastName || ''}
                  formRef={register('recipientDetails.lastName', {
                    required: forSomeoneElse
                      ? "Please enter the recipient's last name"
                      : false,
                    validate: (value: string) =>
                      value.trim().length === 0 && forSomeoneElse
                        ? "Please enter the recipient's last name"
                        : true
                  })}
                  showError={!!errors?.recipientDetails?.lastName}
                  scrollSettings={{
                    scrollOnFocus: true,
                    scrollToElementId: isMobile
                      ? 'recipient-details-section'
                      : 'details-section'
                  }}
                  tabIndex={!isRecipientDetailsExpanded ? -1 : 0}
                />
              </HalfWidthFlexContainer>
              <IntlPhoneNumber
                onNumberChange={handlePhoneNumberOnChange}
                onBlur={handlePhoneInputsBlur}
                formHandle={formHandle}
                prefixDropdownId={phonePrefixId}
                labelText='Phone Number'
                prefixDropdownValue={recipientDetails?.contactNumPrefix}
                prefixDropdownName={'recipientDetails[contactNumPrefix]'}
                phoneNumberInputName={'recipientDetails.contactNumber'}
                phoneNumberInputId={phoneInputId}
                phoneNumberValue={recipientDetails?.contactNumPrefix || ''}
                showError={
                  !!errors?.recipientDetails?.contactNumber ||
                  !!errors?.recipientDetails?.contactNumPrefix
                }
                scrollSettings={{
                  scrollOnFocus: true,
                  scrollToElementId: isMobile
                    ? 'recipient-details-section'
                    : 'details-section'
                }}
                forceCloseDropdown={!isRecipientDetailsExpanded}
                tabIndex={!isRecipientDetailsExpanded ? -1 : 0}
                required={!!forSomeoneElse}
                expandableContainerId={'recipient-details-expandable-div'}
              />
            </InputGroup>
          </InputsContainer>
        </ExpandableDiv>
      </Fields>
      {forSomeoneElse &&
        isInternationalNumber(recipientDetails?.contactNumPrefix) && (
          <AlertMessageWrapper>
            <AlertMessage
              skin='warning'
              type='warning'
              subheading='Delivery couriers may not be able to contact international numbers. You may miss delivery updates and calls.'
            />
          </AlertMessageWrapper>
        )}
    </>
  )
}

const AlertMessageWrapper = styled.div(() => ({
  margin: '16px 0'
}))

const Fields = styled.div(() => ({
  display: 'block',
  paddingTop: '16px'
}))

const InputsContainer = styled.div(() => ({
  paddingTop: '16px'
}))

const CollapsedDetailsContainer = styled.div(() => ({
  paddingTop: '16px'
}))

export default RecipientDetails
